'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

export default function UserLoginPanel() {
  const router = useRouter();
  const [phone, setPhone] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      const token = localStorage.getItem('parkping_user_token');
      if (token) router.replace('/my-portal');
    } catch {
      // ignore
    }
  }, [router]);

  const requestOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await fetch('/api/user/auth/request-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone.trim(),
          cardNumber: cardNumber.trim().toUpperCase(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Failed to send OTP');
      setMaskedEmail(data?.maskedEmail || '');
      setStep('otp');
    } catch (err: any) {
      setError(err?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await fetch('/api/user/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone.trim(),
          cardNumber: cardNumber.trim().toUpperCase(),
          otp: otp.trim(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Invalid OTP');
      if (!data?.token) throw new Error('Authentication failed');

      localStorage.setItem('parkping_user_token', data.token);
      router.push('/my-portal');
    } catch (err: any) {
      setError(err?.message || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-screen bg-[radial-gradient(ellipse_at_top,_#ecfdf3,_#f8fafc_45%,_#ffffff)] pt-28 pb-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          <div className="rounded-3xl border border-emerald-100 bg-white shadow-[0_20px_60px_rgba(22,101,52,0.08)] p-8">
            <div className="mb-6">
              <p className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold">Secure Access</p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">User Login Portal</h1>
              <p className="mt-2 text-sm text-slate-500">
                Login with registered mobile number and card number. OTP will be sent to your registered email.
              </p>
            </div>

            {step === 'credentials' ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Registered Mobile Number</label>
                  <div className="mt-2 flex">
                    <span className="inline-flex items-center px-4 rounded-l-xl border border-r-0 border-slate-200 bg-slate-50 text-slate-600">+91</span>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="w-full rounded-r-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                      placeholder="10-digit number"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">Card Number</label>
                  <input
                    value={cardNumber}
                    onChange={(e) => setCardNumber(e.target.value.toUpperCase())}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 font-mono tracking-wide focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    placeholder="e.g. 0102"
                  />
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <button
                  onClick={requestOtp}
                  disabled={loading || phone.length !== 10 || cardNumber.trim().length < 3}
                  className="w-full rounded-xl bg-emerald-600 py-3 font-semibold text-white disabled:opacity-60"
                >
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 px-4 py-3 text-sm">
                  OTP sent to: <b>{maskedEmail || 'your registered email'}</b>
                </p>
                <div>
                  <label className="text-sm font-medium text-slate-700">Enter OTP</label>
                  <input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 tracking-[0.35em] text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    placeholder="------"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setStep('credentials')}
                    className="rounded-xl border border-slate-200 py-3 font-medium text-slate-700"
                  >
                    Back
                  </button>
                  <button
                    onClick={verifyOtp}
                    disabled={loading || otp.length !== 6}
                    className="rounded-xl bg-emerald-600 py-3 font-semibold text-white disabled:opacity-60"
                  >
                    {loading ? 'Verifying...' : 'Login'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-900 text-white p-8 flex flex-col justify-between">
            <div>
              <h2 className="text-2xl font-bold">Why this login is secure</h2>
              <ul className="mt-6 space-y-4 text-sm text-slate-300">
                <li className="flex gap-3"><Icon name="ShieldCheckIcon" size={18} className="text-emerald-400 mt-0.5" />Mobile + Card number match required</li>
                <li className="flex gap-3"><Icon name="EnvelopeIcon" size={18} className="text-emerald-400 mt-0.5" />OTP goes only to registered email</li>
                <li className="flex gap-3"><Icon name="ClockIcon" size={18} className="text-emerald-400 mt-0.5" />OTP expires in 10 minutes</li>
                <li className="flex gap-3"><Icon name="LockClosedIcon" size={18} className="text-emerald-400 mt-0.5" />Signed session token access</li>
              </ul>
            </div>
            <p className="text-xs text-slate-400">Need help? support@parkping.co.in</p>
          </div>
        </div>
      </div>
    </section>
  );
}
